import React, { useContext, useEffect } from 'react';
import { ReactComponent as RecentViewedIcon } from '@assets/svg/recent-viewed.svg';
import { ReactComponent as RecentSearchIcon } from '@assets/svg/recent-searches.svg';
import Highlighter from 'react-highlight-words';
import {
  updateRecentViewed,
  VIEW_LIMIT,
} from '@store/redux/features/search/searchSlice';
import { useAppDispatch } from '@store/redux/store';
import { useHistory } from 'react-router-dom';
import ProductImage from '@assets/png/product.png';
import { getLastWord } from '@utils/getLastWord/getLastWord';
import { NavigationContext } from '@layout/navigation/navigation';
import { PartialSetter, IFilterOpts } from '@pages/Products/Heading.types';
import { SEARCH_INVENTORY_KEY } from '@pages/Products/Products';
import {
  StyledOption,
  Container,
  Header,
  RowDiv,
  Image,
  Margin,
  SerialEmpty,
  SetMaxWeight,
  StyledOptionAutoComplete,
} from './SearchBarExpansiveWithHistory.styles';
import { IAutocompleteContext } from '../Autocomplete/types';
import { makeGetNewInput } from './makeGetNewInput';
import { shouldShowHistory } from './shouldShowHistory';

interface InventoryAutocompleteItemsProps {
  // Provide through ctx.
  autocompleteProps: IAutocompleteContext;
  broadcastFilterOpts: PartialSetter<IFilterOpts>;
  // onClickFilter
  filterOpts: IFilterOpts;
}
export function InventoryAutocompleteItems(
  props: InventoryAutocompleteItemsProps,
) {
  const { autocompleteProps, broadcastFilterOpts, filterOpts } = props;
  const { studioId } = useContext(NavigationContext);

  const { broadcast, activeOption, onChange, searchText, startSearch } =
    autocompleteProps;
  const { autocompleteOptions, recentSearches, recentViewed } = filterOpts;
  const showHistory = shouldShowHistory(
    recentSearches,
    recentViewed,
    autocompleteOptions,
  );

  const dispatch = useAppDispatch();
  const history = useHistory();

  function maxPossible() {
    let limit = -1;
    if (autocompleteOptions.length > 0) limit += VIEW_LIMIT;
    if (recentSearches.length > 0) limit += VIEW_LIMIT;
    return limit;
  }

  // Autocomplete invariants.
  useEffect(() => {
    // update max/min as data changes
    const autocompleteLengths =
      autocompleteOptions.length + recentSearches.length - 1;
    broadcast({
      max: Math.min(autocompleteLengths, maxPossible()),
    });
  }, [autocompleteOptions, recentSearches]);

  useEffect(() => {
    // Set value as we go through options
    const newInput = makeGetNewInput(
      autocompleteOptions,
      recentSearches,
      activeOption,
    );
    if (activeOption > -1) onChange(newInput());
  }, [activeOption]);
  useEffect(() => {
    // notify if empty
    broadcast({ isEmpty: !showHistory });
  }, [showHistory]);

  if (!showHistory) return null;
  return (
    <Container>
      {autocompleteOptions && autocompleteOptions.length > 0 && (
        <div>
          {autocompleteOptions.slice(0, VIEW_LIMIT).map((item, index) => (
            <StyledOptionAutoComplete
              key={item.id}
              isActive={index === activeOption}
              onClick={() => {
                startSearch(item.labelOne);

                broadcastFilterOpts({
                  recentSearches: [],
                  recentViewed: [],
                  autocompleteOptions: [],
                });
                const searchBar = document.getElementById('myTextField');
                searchBar && searchBar.focus();
              }}
            >
              <Highlighter
                highlightClassName="text"
                searchWords={[getLastWord(searchText)]}
                autoEscape
                textToHighlight={item.labelOne}
              />
            </StyledOptionAutoComplete>
          ))}
        </div>
      )}
      {recentSearches && recentSearches.length > 0 && (
        <div>
          <RowDiv>
            <RecentSearchIcon />
            <Header>Recent Searches</Header>
          </RowDiv>
          {recentSearches.slice(0, VIEW_LIMIT).map((item, index) => (
            <StyledOptionAutoComplete
              key={item}
              isActive={
                autocompleteOptions
                  ? index === activeOption - autocompleteOptions.length
                  : autocompleteOptions === activeOption
              }
              onClick={() => {
                broadcastFilterOpts({
                  recentSearches: [],
                  recentViewed: [],
                  autocompleteOptions: [],
                });

                startSearch(item);
              }}
            >
              <Highlighter
                highlightClassName="text"
                searchWords={[searchText]}
                autoEscape
                textToHighlight={item}
              />
            </StyledOptionAutoComplete>
          ))}
        </div>
      )}
      {recentViewed && recentViewed.length > 0 && (
        <div>
          <RowDiv className="secondItem">
            <RecentViewedIcon />
            <Header>Recently Viewed</Header>
          </RowDiv>
          {recentViewed
            .slice(0, VIEW_LIMIT)
            .filter(e => e.studioId === studioId)
            .map(item => (
              <StyledOption
                key={item.link}
                onClick={() => {
                  broadcastFilterOpts({
                    recentSearches: [],
                    recentViewed: [],
                    autocompleteOptions: [],
                  });

                  dispatch(
                    updateRecentViewed({
                      keyHistory: SEARCH_INVENTORY_KEY,
                      value: item,
                    }),
                  );
                  history.push(item.link);
                }}
              >
                {item.thumbnail ? (
                  <Image src={item.thumbnail} alt="" />
                ) : (
                  <Image className="defaultImage" src={ProductImage} alt="" />
                )}
                <SetMaxWeight>
                  <Highlighter
                    highlightClassName="text"
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={`${item.labelOne}`}
                  />
                </SetMaxWeight>
                <Margin />
                {item.labelTwo ? (
                  <Highlighter
                    highlightClassName="text"
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={item.labelTwo}
                  />
                ) : (
                  <SerialEmpty>Serial # Empty</SerialEmpty>
                )}
              </StyledOption>
            ))}
        </div>
      )}
    </Container>
  );
}
